import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { RECORDING_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { VideoSession } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { getAuthConfig } from '../../../../data/auth/authSelectors'
import { getInformedConsentBannerDismissed } from '../../../data/ltVideoSelectors'
import { useGetEpisodeQuery } from '../../../episodes/data/episodesApi'
import {
  getConsentRecipientsForSession,
  getInformedConsentBannerForSession,
  SessionInformedConsentBanner,
} from '../../consentUtils'
import { useGetEpisodeConsentsQuery } from '../consentApi'

export type VideoSessionBanners = {
  showRecordingConsentBanner: boolean
  informedConsentBanner: SessionInformedConsentBanner | undefined
}

/**
 * The single place that decides which banner (if any) renders above a video session. Used by both
 * ProviderSession (which renders the banners) and useVideoSessionBannerHeight (which reserves space
 * for them underneath), so the two can never disagree about what is on screen.
 *
 * The appointment decides who is in the session (teen, caregiver, or both for a DBT for Teens family
 * session), and so whose consents the informed-consent banner is about.
 */
export const useVideoSessionBanners = ({
  videoSession,
  appointment,
  clientPronouns,
}: {
  videoSession: VideoSession | undefined | null
  appointment: Appointment | undefined | null
  clientPronouns?: string[]
}): VideoSessionBanners => {
  const { isRecordingConsentBannerEnabled, isConsentTrackingEnabled } = useFlags()
  const config = useSelector(getAuthConfig)
  const isInformedConsentBannerDismissed = useSelector(getInformedConsentBannerDismissed)

  const { data: episode } = useGetEpisodeQuery(
    videoSession?.episode_id ? { episodeId: videoSession.episode_id } : skipToken,
  )
  // currentData (not data) so a previous session's consents are never applied to a new one
  const { currentData: episodeConsentsData } = useGetEpisodeConsentsQuery(
    isConsentTrackingEnabled && videoSession?.episode_id ? { episodeId: videoSession.episode_id } : skipToken,
  )

  return useMemo(() => {
    const episodeForSession = videoSession ? episode : undefined
    const showRecordingConsentBanner =
      !!isRecordingConsentBannerEnabled &&
      videoSession?.recording_status === RECORDING_STATUS.awaiting_recording_consent &&
      episodeForSession?.recording_consent !== false &&
      !config?.recordingDisabled

    const informedConsentBanner =
      showRecordingConsentBanner || !isConsentTrackingEnabled || isInformedConsentBannerDismissed
        ? undefined
        : getInformedConsentBannerForSession(
            episodeConsentsData?.consents,
            getConsentRecipientsForSession(appointment, videoSession, clientPronouns),
          )

    return { showRecordingConsentBanner, informedConsentBanner }
  }, [
    appointment,
    clientPronouns,
    config?.recordingDisabled,
    episode,
    episodeConsentsData?.consents,
    isConsentTrackingEnabled,
    isInformedConsentBannerDismissed,
    isRecordingConsentBannerEnabled,
    videoSession,
  ])
}
